'use client';

import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { AuthProvider } from '@/contexts/AuthContext';
import { DateProvider } from '@/contexts/DateContext';
import { RightPanelProvider } from '@/contexts/RightPanelContext';
import ContentProtection from '@/components/ContentProtection';

type Theme = 'light' | 'dark';

interface ThemeContextType {
  theme: Theme;
  toggleTheme: (theme: Theme) => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export function Providers({ children }: { children: ReactNode }) {
  const [theme, setTheme] = useState<Theme>('light');
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    // Use requestIdleCallback to avoid blocking initial render
    const initTheme = () => {
      setMounted(true);
      const savedTheme = localStorage.getItem('theme') as Theme | null;
      if (savedTheme) {
        setTheme(savedTheme);
        document.documentElement.classList.toggle('dark', savedTheme === 'dark');
      } else {
        const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
        const initialTheme = prefersDark ? 'dark' : 'light';
        setTheme(initialTheme);
        document.documentElement.classList.toggle('dark', initialTheme === 'dark');
      }
    };

    // Execute immediately for theme (critical for preventing flash)
    if (typeof window !== 'undefined') {
      initTheme();
    }
  }, []);

  const toggleTheme = (newTheme: Theme) => {
    setTheme(newTheme);
    if (mounted) {
      localStorage.setItem('theme', newTheme);
      document.documentElement.classList.toggle('dark', newTheme === 'dark');
    }
  };

  // Always provide the context, even when not mounted
  // This prevents the "useTheme must be used within a Providers" error
  return (
    <ThemeContext.Provider value={{ theme, toggleTheme }}>
      <ContentProtection />
      <AuthProvider>
        <DateProvider>
          <RightPanelProvider>
            {children}
          </RightPanelProvider>
        </DateProvider>
      </AuthProvider>
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a Providers');
  }
  return context;
}

